import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart } from "../../../models/chart_model";
import { getGridcoDiscomData, getGridcoScheduleData, getDiscomLoadMetrics } from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { GROUP_BY } from "./Data";
import { GridcoDiscomData } from "../../../models/gridco";
import { DISCOM } from "./Data";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { GridcoPeakAnalysis } from "./Peak_Discom";
import { GridcoSummaryAnalysis } from "./Discom_Analysis_Summary";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { DiscomGridcoData } from "../../../models/metrics";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
const UNITS = ["MW", "MWh", "LU"];

const X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const TYPE = ["Drawal", "Schedule"];

// Moved outside component — not recreated on every render
const BLOCKS_96 = Array.from({ length: 96 }, (_, i) => i + 1);

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatMonthYear(dateStr: string): string {
  const d = new Date(dateStr);
  return `${MONTH_NAMES[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

const CHART_SUFFIXES = [
  { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
  { suffix: "schedule", displayName: "Schedule", stackSuffix: "schedule" },
];

function createChartObject(
  key: string,
  index: number,
  suffix: string,
  displayName: string,
  stackSuffix: string,
  selectedChartType: { value: string; stackId?: string },
  colors: string[],
  unit: string
) {
  return {
    dataKey: `${key}_${suffix}_${unit.toLowerCase()}`,
    name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
    // legendColor: undefined,
    // stroke:
    //   suffix === "schedule"
    //     ? lightenColor(colors[index], 25)
    //     : suffix === "drawal"
    //     ? colors[index]
    //     : stringToConsistentColor(`${key}_${suffix}`),
    type: selectedChartType.value,
    stackId:
      selectedChartType.stackId !== undefined ? stackSuffix : undefined,
  };
}

export function GridcoDiscomAnalysis() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [drawalData, setDrawalData] = useState<GridcoDiscomData[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [loading, setLoading] = useState(true);
  const [selectedSuffix, setSelectedSuffix] = useState([CHART_SUFFIXES[0]]);
    const [metrics, setMetrics] = useState<DiscomGridcoData | null>(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    
    const fetchMetrics = useCallback(async () => {
      if (!DefaultDates[activeTab]?.defaultRangeDate) {
        setMetrics(null);
        return;
      }
  
      setMetricsLoading(true);
      try {
        const dates = DefaultDates[activeTab].defaultRangeDate;
        const startDateStr = formatDateForAPI(dates.startDate);
        const endDateStr = formatDateForAPI(dates.endDate);
  
        const metricsData = await getDiscomLoadMetrics({
          ranges: [
            {
              startDate: startDateStr,
              endDate: endDateStr,
              blocks: selectedBlock.map((block) => block.value),
            },
          ],
          aggregated: true,
          discom: selectedDiscom.length > 0 ? selectedDiscom : undefined,
        });
  
        setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetrics(null);
      } finally {
        setMetricsLoading(false);
      }
    }, [DefaultDates, activeTab, selectedBlock, selectedDiscom]);
  
    useEffect(() => {
      fetchMetrics();
    }, [fetchMetrics]);
  

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDate = dates.startDate.toISOString().split("T")[0];
    const endDate = dates.endDate.toISOString().split("T")[0];

    try {
      const [fetchedDrawalData, fetchedScheduleData] = await Promise.all([
        getGridcoDiscomData(startDate, endDate),
        getGridcoScheduleData(startDate, endDate)
      ]);

      setDrawalData(fetchedDrawalData);
      setScheduleData(fetchedScheduleData);

      // Single-pass date/block extraction using Sets
      const dateSet = new Set<string>();
      const blockSet = new Set<number>();

      for (const item of fetchedDrawalData) {
        dateSet.add(item.date);
        blockSet.add(item.block);
      }
      for (const item of fetchedScheduleData) {
        dateSet.add(item.date);
        blockSet.add(item.block);
      }

      setUniqueDate(
        Array.from(dateSet).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
      );
      setUniqueBlock(Array.from(blockSet).sort((a, b) => a - b));
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // Pre-compute filter value sets once, not inside every .filter() call
  const selectedDateValues = useMemo(
    () => new Set(selectedDate.map((item) => item.value)),
    [selectedDate]
  );
  const selectedBlockValues = useMemo(
    () => new Set(selectedBlock.map((item) => item.value)),
    [selectedBlock]
  );

  useEffect(() => {
    const isDrawalSelected = selectedSuffix.some(s => s.suffix === "drawal");
    const isScheduleSelected = selectedSuffix.some(s => s.suffix === "schedule");

    // O(n+m) merge using a Map — replaces the previous O(n×m) find() loop
    // Use "|" separator to avoid collision with discom names that may contain "_"
    const mergedDataMap = new Map<string, any>();

    for (const d of drawalData) {
      const key = `${d.date}|${d.block}|${d.discom}`;
      mergedDataMap.set(key, {
        date: d.date,
        block: d.block,
        discom: d.discom,
        drawal_mw: d.drawal_mw,
        drawal_mwh: d.drawal_mwh,
        drawal_lu: d.drawal_mwh / 100,
        schedule_mw: 0,
        schedule_mwh: 0,
        schedule_lu: 0,
        month: formatMonthYear(d.date),
        dates: d.dates,
      });
    }

    for (const s of scheduleData) {
      const key = `${s.date}|${s.block}|${s.discom}`;
      const existing = mergedDataMap.get(key);
      if (existing) {
        existing.schedule_mw = s.schedule_mw;
        existing.schedule_mwh = s.schedule_mwh;
        existing.schedule_lu = s.schedule_mwh / 100;
      } else {
        mergedDataMap.set(key, {
          date: s.date,
          block: s.block,
          discom: s.discom,
          drawal_mw: 0,
          drawal_mwh: 0,
          drawal_lu: 0,
          schedule_mw: s.schedule_mw,
          schedule_mwh: s.schedule_mwh,
          schedule_lu: s.schedule_mwh / 100,
          month: formatMonthYear(s.date),
          dates: s.dates,
        });
      }
    }

    let dataToProcess = Array.from(mergedDataMap.values());

    // Filter by selected type
    if (isDrawalSelected && !isScheduleSelected) {
      dataToProcess = dataToProcess.filter(item => item.drawal_mw > 0);
    } else if (!isDrawalSelected && isScheduleSelected) {
      dataToProcess = dataToProcess.filter(item => item.schedule_mw > 0);
    }

    // Apply user filters — use pre-computed Sets for O(1) lookups
    let filteredData = dataToProcess;

    if (selectedDiscom.length > 0) {
      const discomSet = new Set(selectedDiscom);
      filteredData = filteredData.filter(item => discomSet.has(item.discom));
    }
    if (selectedDateValues.size > 0) {
      filteredData = filteredData.filter(item => selectedDateValues.has(item.date));
    }
    if (selectedBlockValues.size > 0) {
      filteredData = filteredData.filter(item => selectedBlockValues.has(item.block));
    }

    // Sort by date then block
    filteredData.sort((a, b) => {
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      return dateCompare !== 0 ? dateCompare : a.block - b.block;
    });

    const getKeys = () => {
      if (selectedInterval.key === "time") return ["date", "block"];
      if (selectedInterval.key === "date") return ["date"];
      return [selectedInterval.values[0]];
    };

    const aggregationKeys: string[] = [];
    const averageKeys: string[] = [];
    for (const { suffix } of selectedSuffix) {
      if (suffix === "drawal") {
        aggregationKeys.push("drawal_mw", "drawal_mwh", "drawal_lu");
        averageKeys.push("drawal_mw");
      }
      if (suffix === "schedule") {
        aggregationKeys.push("schedule_mw", "schedule_mwh", "schedule_lu");
        averageKeys.push("schedule_mw");
      }
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: [selectedGroupKeys.toLowerCase()],
      tableKeys: ["date", "block"],
      volumeKey: ``,
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    drawalData,
    scheduleData,
    selectedGroupKeys,
    selectedDiscom,
    selectedArea,
    selectedDateValues,
    selectedBlockValues,
    selectedInterval,
    selectedUnit,
    selectedSuffix,
  ]);

  function _handleGroupByChange(index: number) {
    const newGroupBy = GROUP_BY[index];
    setSelectedGroupKeys(newGroupBy);
    // If switching to "Total", stacked bar is not meaningful — reset to Line
    if (newGroupBy === "Total" && (selectedChartType as any).stackId !== undefined) {
      setSelectedChartType(R_CHART_TYPES[2]); // Line
    }
  }

  function _handleSuffixChange(index: number) {
    const selectedItem = CHART_SUFFIXES[index];
    if (selectedSuffix.some(item => item.suffix === selectedItem.suffix)) {
      if (selectedSuffix.length === 1) return;
      setSelectedSuffix(selectedSuffix.filter(item => item.suffix !== selectedItem.suffix));
    } else {
      setSelectedSuffix([...selectedSuffix, selectedItem]);
    }
  }

  const xDataKey = useMemo(() => {
    if (!selectedInterval.isTime) return selectedInterval.key;
    return "block";
  }, [selectedInterval.isTime, selectedInterval.key]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  const legends = useMemo(
    () =>
      selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedChartType as any,
            ExchangeColors,
            selectedUnit
          ) as any
        )
      ),
    [selectedSuffix, chartKeys, selectedChartType, selectedUnit]
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);
  const secondXDataKey = selectedInterval.isTime ? "date" : undefined;

  const summaryExtraKeys = useMemo(
    () =>
      selectedInterval.isTime
        ? ["block", selectedInterval.values[1]]
        : [selectedInterval.values[0]],
    [selectedInterval]
  );

  const summaryExtraLabels = useMemo(
    () => selectedInterval.values.map(value => (value === "date" ? "Date" : value)),
    [selectedInterval.values]
  );

  const tablePar = useMemo(
    () => ({
      legends: visibleLegends,
      data: chartData,
      tableData: TableData,
      itemsPerPage: 96,
      unit: selectedUnit,
      extraKeys: summaryExtraKeys,
      extraLabels: summaryExtraLabels,
      filename: "Discom Load",
    }),
    [visibleLegends, chartData, TableData, selectedUnit, summaryExtraKeys, summaryExtraLabels]
  );

  if (selectedXAxisMode.value === "peak") {
    return (
      <GridcoPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        metrics={metrics}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <GridcoSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        metrics={metrics}
      />
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Interval"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(X_AXIS[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              // if (index === 1) return;
              _handleGroupByChange(index);
            }}
          />
          <FilterMenu
            title="Type"
            items={TYPE}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            onSelect={(item, index) => {
              _handleSuffixChange(index);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            disabledItems={selectedGroupKeys === "Total" ? ["Stacked Bar"] : []}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
           <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Peak Load (MW)"].value,
                  unit: metrics.drawal["Peak Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Peak Load (MW)"].value,
                  unit: metrics.schedule["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Average Load (MW)"].value,
                  unit: metrics.drawal["Average Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Average Load (MW)"].value,
                  unit: metrics.schedule["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Minimum Load (MW)"].value,
                  unit: metrics.drawal["Minimum Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Minimum Load (MW)"].value,
                  unit: metrics.schedule["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Total Energy (MU)"].value,
                  unit: metrics.drawal["Total Energy (MU)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Total Energy (MU)"].value,
                  unit: metrics.schedule["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Discom Load"
              subtitle="Fetching Gridco discom drawal and schedule data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  Discom Load ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 w-full justify-end">
                  <BlockRangeSelector
                    blocks={BLOCKS_96}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />

                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => renderHourTick(value, 10)
                      : undefined
                  }
                  xDataKey={xDataKey}
                  secondXDataKey={secondXDataKey}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Gridco : BSP bills & ABT Meters</p>}
              text="1. BSP Bills : Annexure 1 is used for cumulation.
              2. Some meter numbers in Annexure 1 were missing in provided ABT Meter files.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
